import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useRouteMatch, useHistory } from "../../../utils/router";
import Select from "react-select";
import { estado_DGII, estado_factura, getCuentasForDropdown, isDayDate, permisions } from '../../../utils/helpers';
import SelectAsync from "react-select/async";

import {
	getLink,
	formatCurrency,
	toast,
	getZonasForDropdown,
	getVendedoresForDropdown,
	getCategoriasForDropdown,
	isVendedor,
	isVendedorSinCobrador,
	validarPINSeguridad,
	isPastDate,
	bancos,
    getOptionsForDropdown,
    formatNumber,
	getPlazosForDropdown,
} from "../../../utils/helpers";
import PageHeader from "../../general/PageHeader";
import Loader from "../../general/Loader";
import Pagination from "../../general/Pagination";
import Modal from "../../general/Modal";
import { downloadFile, postRequest, request } from "../../../utils/request";
import { apiRequest } from "../../../hooks/apiRequest";
import PrefixedInput from "../../general/PrefixedInput";
import FiltersContainer from "../../general/FiltersContainer";
import useFilters from "../../../hooks/useFilters";
import { auth } from "../../../utils/auth";
import InvalidScreen from "../../general/InvalidScreen";
import Dropdown from "../../general/Dropdown";
import { ESTADOS_TRANSACCION, METODOS_PAGO } from "../../../utils/types";
import PlanificadorTareaEstatus from "./Components/PlanificadorTareaEstatus";
import PagoForm from "../Components/PagoForm";

const formatDateTime = (date) => {
	return dayjs(date).format("DD/MM/YYYY hh:mm a");
};

const obtenerTextoEstado = (factura) => {
	const estado = factura.estado;

	// Verificar si la factura fue afectada 100% por notas de crédito (no canceladas)
	const notasActivas = factura.notas_credito?.filter(
		(nc) => nc.estado !== "cancelada"
	) || [];
	const totalNotasCredito = notasActivas.reduce(
		(acc, nc) => acc + parseFloat(nc.total || 0),
		0
	);
	const afectadaPorNC =
		estado === "pagada" &&
		notasActivas.length > 0 &&
		totalNotasCredito >= parseFloat(factura.total || 0);

	if (afectadaPorNC) {
		return "NC";
	}

	return estado;
};

const renderEstadoBadge = (factura) => {
	const estado = factura.estado;
	let color = "badge-danger";
	const texto = obtenerTextoEstado(factura);

	if (texto === "NC") {
		color = "badge-warning";
	} else if (estado === "pendiente") {
		color = "badge-secondary";
	} else if (estado === "procesando") {
		color = "badge-warning";
	} else if (estado === "pagada") {
		color = "badge-success";
	}

	return <span className={`badge ${color} p-2 text-capitalize`} style={{width: "80px"}}>
		{factura.estado === 'procesando' && texto !== "NC" ? (
			<a className="ml-1" href={getLink(`/cheques/${factura.cheques?.find(c => c.status === 'procesando')?.id}`)} target="_blank" style={{color: "inherit"}}>{estado}</a>
		) : texto}
	</span>;
};

const canValidate = (factura) =>
	factura.recurrencia_id &&
	factura.validada === "no" &&
	factura.estado === "pendiente";

const canPay = (factura) => {
    return (factura.por_cobrar === factura.total) && (factura.estado === "pendiente" &&
	dayjs().startOf("day") < dayjs(factura.fecha_emision).endOf("day"));
}

const canPrint = (factura) =>
	!!factura?.tipo_pago &&
	factura?.tipo_pago !== "efectivo" &&
	factura.estado !== "pagada";

const Facturas = () => {
	// Route  matcher
	let match = useRouteMatch();
	const history = useHistory();
	const formRef = useRef();
	const formInfoPagoRef = useRef();

	const { filters, handleFilterChange, filtersQuery } = useFilters({
		buscar: "",
		vendedor: isVendedor() ? auth.getUser().empleado?.id : "",
		estado: "",
		metodo_pago: "",
		estadoDgii: "",
		desde: dayjs().format("YYYY-MM-DD"),
		hasta: dayjs().format("YYYY-MM-DD"),
		super_filter: "",
	});

	const [facturas, setFacturas] = useState({});
	const [vendedores, setVendedores] = useState([]);
	const [empleados, setEmpleados] = useState([]);
	const [zonas, setZonas] = useState([]);
	const [categorias, setCategorias] = useState([]);
	const [plazos, setPlazos] = useState([]);
	const [numeraciones, setNumeraciones] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
	const [showAgregarPagoModal, setShowAgregarPagoModal] = useState(false);
	const [showValidateModal, setShowValidateModal] = useState(false);
	const [showInformacionDgiiModal, setShowInformacionDgiiModal] = useState(false);
	const [printFacturaTipo, setPrintFacturaTipo] = useState("normal");
	const [facturaSeleccionada, setFacturaSeleccionada] = useState({});
	const [pagoError, setPagoError] = useState({});
	const [pagoInfo, setPagoInfo] = useState({
		pago: 0,
		tipo_pago: null,
	});
	const [pinSeguridad, setPinSeguridad] = useState("");
	const [showPINModal, setShowPINModal] = useState(false);
	const [tipoImpresionAfterPago, setTipoImpresionAfterPago] = useState("normal");

	const [showModalRutaDistribucion, setShowModalRutaDistribucion] = useState(false);
	const [desdeRD, setDesdeRD] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
	const [hastaRD, setHastaRD] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
	const [empleadoRD, setEmpleadoRD] = useState();
	const [showCancel, setShowCancel] = useState(false);

	const [showModalTipoDespacho, setShowModalTipoDespacho] = useState(false);
	const [facturaTipoDespacho, setFacturaTipoDespacho] = useState(null);

	const getFacturas = (url) => {
		url = url ? url + "&limit=15" : "facturas?limit=15";
		setIsLoading(true);
		// setTempUrl(url);

		request(url).then((res) => {
			setFacturas(res);
			setIsLoading(false);
		});
	};
	const [razonCancelacion, setRazonCancelacion] = useState(null);
	const [facturaActual, setFacturaActual] = useState(null);

	// Sin debounce propio: useFilters ya espera antes de rearmar el query, y este
	// envoltorio —reconstruido en cada render— sólo añadía otro temporizador que
	// descartaba el filtro anterior si cambiabas dos seguidos.
	const onFilterChange = (value, filtro) =>
		handleFilterChange(
			filtro,
			value instanceof Array ? value.map((v) => v.value).join(",") : value
		);

	const openCancelModal = (factura) => {
		setFacturaActual(factura);
		setShowCancel(true);
	};

	const closeCancelModal = () => {
		setShowCancel(false);
		setFacturaActual(null);
	};

	const openPagarFacturaModal = (factura) => {
		setFacturaSeleccionada(factura);
		setPagoInfo((state) => ({ tipo_pago: factura.tipo_pago }));
		setShowAgregarPagoModal(true);
	};

	const openValidarFacturaModal = (factura) => {
		setFacturaSeleccionada(factura);
		setShowValidateModal(true);
	};

	const validateFactura = async (facturaId) => {
		const response = await apiRequest(
			null,
			`facturas/${facturaId}/validate`,
			"PUT"
		);

		if (response.success) {
			getFacturas(`facturas${filtersQuery}`);
			setShowValidateModal(false);
			toast("Factura validada");
			return;
		}

		toast("Error Validando Factura", "error");
	};

    const actualizarEstado = async (id) => {
        const response = await apiRequest(
            null,
			`alanube/documentos/${id}`,
            'GET'
		);
        if (response?.success) {
            getFacturas(`facturas${filtersQuery}`);
            toast("Estado actualizado", "success");
        } else {
            toast(response?.data || "Error actualizando estado", 'error');
        }
    }

    const reenviarDGII = async (id) => {
        const response = await apiRequest(
            {},
			`alanube/documentos/reenviar/${id}`,
            'POST'
		);

        if (!response?.data?.id) {
            toast(response.data, 'error')
        } else {
            actualizarDocumento(response.data);
        }
    }

    const actualizarDocumento = (documento, tipo = 'documento') => {
        setFacturas(prev => ({
            ...prev,
            data: prev.data?.map(f => (
                f.id === documento.documento_id ? { ...f, [tipo]: documento } : f
            )),
        }))
    }

    const openInformacionDgiiModal = (factura) => {
		setFacturaSeleccionada(factura);
		setShowInformacionDgiiModal(true);
	};

	const guardarPago = async (event) => {
		event.preventDefault();
        setSaving(true);

		if (!pagoInfo.pago || pagoInfo.pago <= 0 || pagoInfo.pago < facturaSeleccionada.por_cobrar) {
			toast("El monto debe ser mayor o igual al total de la factura.", "error");
			setSaving(false);
			return;
		}

		const data = new FormData(event.target);

		const response = await apiRequest(
			data,
			`agregar-pago-factura/${facturaSeleccionada.id}`,
			"POST"
		);

		if (response.success) {
			toast("Pago Agregado", "success");
			setShowAgregarPagoModal(false);
			setFacturaSeleccionada({});
			setPagoInfo({ pago: null, tipo_pago: null });
			getFacturas(`facturas${filtersQuery}`);
			if (tipoImpresionAfterPago === "doble") {
				window.open(
					getLink(
						`/imprimir/facturas/${facturaSeleccionada.id}/doble`
					)
				);
				return;
			}
			window.open(
				getLink(`/imprimir/facturas/${facturaSeleccionada.id}`)
			);
		} else if (response.code === 422) {
			setPagoError(response.data.message);
		} else {
            toast(response.data.message, "error");
        }

        setSaving(false);
	};

	const setPorPagar = () => {
		setPagoInfo((state) => ({
			...state,
			pago: facturaSeleccionada.por_cobrar,
		}));
	};

	const openPINModal = (factura) => {
		setFacturaSeleccionada(factura);
		setShowPINModal(true);
	};

	const checkPINSeguridad = async () => {
		if (await validarPINSeguridad(pinSeguridad)) {
			setPinSeguridad("");
			setShowPINModal(false);
			// history.push(
			// 	getLink(`/ingresos/facturas/${facturaSeleccionada.id}/edit`)
			// );
			openCancelModal(facturaSeleccionada)
			return;
		}

		toast("PIN incorrecto, intente de nuevo", "error");
	};

    const displayFilters = () => {
        document.getElementById('mas-filtros').classList.toggle('d-none');
        document.querySelectorAll('.filtros-principales-movil').forEach((el) =>
            el.classList.toggle('d-none')
        );
    };

	const openModalRutaDistribucion = () => {
		setShowModalRutaDistribucion(true);
	};

	const downloadRutaDistribucionReporte = async () => {
		if (!desdeRD || !hastaRD || !empleadoRD) {
			toast("Ingrese desde, hasta y Empleado.", 'error');
			return;
		}

		await downloadFile(`reportes/ruta-distribucion${filtersQuery}&desde=${desdeRD}&hasta=${hastaRD}&empleadoId=${empleadoRD.value}&empleadoName=${empleadoRD.label}`, 'ruta_distribucion.xls');

		setShowModalRutaDistribucion(false);
	};

	const downloadPlanificacionTareasReporte = async () => {
		await downloadFile(`reportes/planificacion-tareas${filtersQuery}`, 'planificacion_tareas.xls');
	};

	const getEmpleadosForDropdown = async () => {
		const response = await request("empleados?limit=1000");
		const filteredResponse = response.data.filter(empleado => empleado !== null);

		setEmpleados([
			...filteredResponse.map((empleado) => ({
			value: empleado?.id || null,
			label: `${empleado?.nombre} ${empleado?.apellido}`,
			}))
		]);
	};

	const cancelFactura = async (facturaID) => {
		if (!razonCancelacion) {
			toast('Favor seleccionar el motivo de la cancelación.', 'error');
			return;
		}

		const data = new FormData();

		data.append("_method", 'DELETE');
		data.append("razon_estado", razonCancelacion);

		const response = await apiRequest(
			data,
			`facturas/${facturaID}`,
			'POST'
		);

		if (response.success) {
			toast("Factura Cancelada", "success");
			history.push(getLink("/ingresos/facturas"));

			getFacturas(`facturas${filtersQuery}`);

			closeCancelModal()
			return;
		}
		toast("Error cancelando factura", "error");
	};

	const openModalToggleTipoDespacho = (factura) => {
		setFacturaTipoDespacho(factura);
		setShowModalTipoDespacho(true);
	}

	const closeModalToggleTipoDespacho = () => {
		setFacturaTipoDespacho(null);
		setShowModalTipoDespacho(false);
	}

	const toggleTipoDespacho = async () => {
		const response = await postRequest(`facturas/${facturaTipoDespacho.id}/toggle-despacho`);

		if (response.success) {
			toast("Tipo de entrega actualizado", "success");
			getFacturas(`facturas${filtersQuery}`);
			closeModalToggleTipoDespacho();
		} else {
			toast("Error actualizando tipo de entrega", "error");
		}
	}

	useEffect(() => {
		// if (filtersQuery === "") return;
		getFacturas(`facturas${filtersQuery}`);
	}, [filtersQuery]);

	useEffect(() => {
		getCategoriasForDropdown().then((data) => {
			setCategorias(data);
		});
		getPlazosForDropdown().then((data) => {
			setPlazos(data);
		});
		getOptionsForDropdown('numeracion-documentos?tipo_documento=factura_venta').then((data) => {
			setNumeraciones(data);
		});
		getEmpleadosForDropdown();
		if (isVendedor()) {
			const rutas = JSON.parse(localStorage.getItem("rutas"));
			setZonas(rutas);
		} else {
			getZonasForDropdown().then((data) => {
				setZonas(data);
			});
			getVendedoresForDropdown().then((data) => {
				setVendedores(data);
			});
		}
	}, []);

	/*if (isVendedor() && !localStorage.getItem("rutas")) {
		return <InvalidScreen message="No hay zonas seleccionadas" />;
	}*/

    const user = auth.getUser();
    const sucursalActual = user.sucursales.filter(s => s.id === user.sucursalActual)[0];

	return (
		<>
			<PageHeader title="Facturas">
				<div className="col-12 col-lg-3 pr-md-0 d-none d-lg-block">
					<label
						htmlFor="desde"
						style={{ fontSize: "12px", textTransform: "uppercase" }}
					>
						Consulta
					</label>
					<input
						onChange={(e) =>
							onFilterChange(e.target.value, "super_filter")
						}
						type="search"
						placeholder="Consultar..."
						className="form-control"
						name="super_filter"
					/>
				</div>
				<div className="col-12 col-md-6 col-lg-2 pr-md-0 d-none d-lg-block">
					<label
						htmlFor="desde"
						style={{ fontSize: "12px", textTransform: "uppercase" }}
					>
						Desde
					</label>
					<input
						type="date"
						name="desde"
						id="desde"
						value={filters.desde}
						className="form-control"
						onChange={(e) =>
							onFilterChange(e.target.value, "desde")
						}
					/>
				</div>
				<div className="col-12 col-md-6 col-lg-2 d-none d-lg-block">
					<label
						htmlFor="hasta"
						style={{ fontSize: "12px", textTransform: "uppercase" }}
					>
						Hasta
					</label>
					<input
						type="date"
						name="hasta"
						id="hasta"
						value={filters.hasta}
						className="form-control"
						onChange={(e) =>
							onFilterChange(e.target.value, "hasta")
						}
					/>
				</div>
                <div className="d-flex reportes-anadir-movil">
                    <div className="dropdown ml-3 m-r-10 mt-2 d-lg-block" style={{flex: 1}}>
                        <button className="au-btn au-btn-icon au-btn-filter d-inline-block w-100">
                            <i className="zmdi zmdi-print"></i> Reportes
                        </button>
                        <ul
                            className="dropdown-menu left-0 md-right-0"
                        >
                            <li>
                                {auth.can(permisions.reporteFacturaReporteFacturas) && <Link
                                    to={getLink(
                                        `/imprimir/facturas${filtersQuery}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Reporte Facturas
                                </Link>}
                            </li>
                            <li>
                                {auth.can(permisions.reporteFacturaResumenFacturas) && <Link
                                    to={getLink(
                                        `/imprimir/facturas-resumen${filtersQuery}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Resumen Facturas
                                </Link>}
                            </li>
                            <li>
                                {auth.can(permisions.reporteFacturaDetalleImpuesto) && <Link
                                    to={getLink(
                                        `/imprimir/facturas-detalles-2${filtersQuery}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Detalle de Impuestos
                                </Link>}
                            </li>
                            <li>
                                {(auth.can(permisions.reporteFacturaCuadreCaja) || auth.esVendedor()) && <Link
                                    to={getLink(
                                        `/imprimir/cuadre-diario${filtersQuery}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Cuadre de Caja
                                </Link>}
                            </li>
                                            <li>
                                {auth.can(permisions.reporteFacturaCuadreCajaVentas) && <Link
                                    to={getLink(
                                        `/imprimir/cuadre-caja-ventas${filtersQuery}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Cuadre de Caja de ventas
                                </Link>}
                            </li>
                            <li>
                                {auth.can(permisions.reporteFacturaCuadreCajaResumido) && <Link
                                    to={getLink(
                                        `/imprimir/cuadre-diario-resumido${filtersQuery}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Cuadre de Caja Resumido
                                </Link>}
                            </li>
                            <li>
                                {auth.can(permisions.reporteFacturaListaCarga) && <Link
                                    to={getLink(
                                        `/imprimir/lista-carga${filtersQuery}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Lista de carga
                                </Link>}
                            </li>
                            {!user.bloquear_balance && <>
                                <li>
                                    {auth.can(permisions.reporteFacturaBalance) && <Link
                                        to={getLink(`/imprimir/balance${filtersQuery}`)}
                                        target="_blank"
                                        className="dropdown-item"
                                    >
                                        Balance
                                    </Link>}
                                </li>
                                <li>
                                    {auth.can(permisions.reporteFacturaBalanceDetalle) && <Link
                                        to={getLink(`/imprimir/balance-detalle${filtersQuery}`)}
                                        target="_blank"
                                        className="dropdown-item"
                                    >
                                        Balance Detalle
                                    </Link>}
                                </li>
                            </>}
                            <li>
                                {auth.can(permisions.reporteFacturaRutaDistribucion) && <button
                                    onClick={openModalRutaDistribucion}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Ruta de distribución
                                </button>}
                            </li>
                            <li>
                                {auth.can(permisions.reporteFacturaPlanificacionTareas) && <button
                                    onClick={downloadPlanificacionTareasReporte}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Planificación de tareas
                                </button>}
                            </li>
                        </ul>
                    </div>
                    <Link
                        className="au-btn au-btn-icon au-btn--green au-btn--small m-r-15 ml-auto mt-2 mt-lg-0"
                        to={getLink('/ingresos/facturas/crear')}
                    >
                        <i className="zmdi zmdi-plus" />
                        Añadir Factura
                    </Link>
                </div>
			</PageHeader>
			<FiltersContainer>
				<div className="col-12 d-lg-none">
					<div className="row">
						<div className="col-12">
							<label className="orden-filter-label">Consulta</label>
							<input
								onChange={(e) =>
									onFilterChange(e.target.value, "super_filter")
								}
								type="search"
								placeholder="Consultar..."
								className="form-control"
								name="super_filter"
							/>
						</div>
						<div className="col-6">
							<label className="orden-filter-label" htmlFor="desde-movil">Desde</label>
							<input
								type="date"
								name="desde"
								id="desde-movil"
								value={filters.desde}
								className="form-control"
								onChange={(e) =>
									onFilterChange(e.target.value, "desde")
								}
							/>
						</div>
						<div className="col-6">
							<label className="orden-filter-label" htmlFor="hasta-movil">Hasta</label>
							<input
								type="date"
								name="hasta"
								id="hasta-movil"
								value={filters.hasta}
								className="form-control"
								onChange={(e) =>
									onFilterChange(e.target.value, "hasta")
								}
							/>
						</div>
					</div>
				</div>
				<div className="col">
					<label className="orden-filter-label">Buscar</label>
					<input
						onChange={(e) =>
							onFilterChange(e.target.value, "buscar")
						}
						type="search"
						placeholder="Buscar..."
						className="form-control"
						name="buscar"
					/>
				</div>
				<div className="col-12 col-md-6 col-lg-2 filtros-principales-movil d-none d-lg-block">
					<label className="orden-filter-label">Tipo de pago</label>
					<Select
						id="metodo_pago"
						options={[
							{ value: "", label: "Todos" },
							...METODOS_PAGO,
						]}
						name="metodo_pago"
						onChange={(e) => onFilterChange(e.value, "metodo_pago")}
						placeholder="Todos"
						styles={{
							control: (styles) => ({
								...styles,
								height: "38px",
							}),
						}}
					/>
				</div>
				<div className="col-12 col-md-6 col-lg-2 filtros-principales-movil d-none d-lg-block">
					<label className="orden-filter-label">Estado</label>
					<Select
						id="estado"
						options={[
							{ value: "", label: "Todos" },
							...ESTADOS_TRANSACCION,
							{ value: "nota_credito", label: "Nota de Crédito" }
						]}
						name="estado"
						isMulti
						isClearable
						onChange={(values) => onFilterChange(values, "estado")}
						placeholder="Todos"
					/>
				</div>
				
				<div className="col-12 col-md-6 col-lg-2 filtros-principales-movil d-none d-lg-block">
					<label className="orden-filter-label">Zona</label>
					<Select
						id="zona"
						options={zonas}
						name="zona"
						onChange={(values) => onFilterChange(values, "zona")}
						placeholder="Todos"
						isClearable
						isMulti
					/>
				</div>
				{!isVendedorSinCobrador() && (<div className="col-12 col-md-6 col-lg-2 filtros-principales-movil d-none d-lg-block">
					<label>Vendedor</label>
					<Select
						id="vendedor"
						options={vendedores}
						name="vendedor"
						placeholder="Todos"
						onChange={(e) => onFilterChange(e.value, "vendedor")}
						styles={{
							control: (styles) => ({
								...styles,
								height: "38px",
							}),
						}}
					/>
				</div>)}
                <div className="col-12 col-md-6 col-lg-2 filtros-principales-movil d-none d-lg-block">
                    <label className="orden-filter-label">Categoría</label>
                    <Select
                        id="categoria"
                        options={categorias}
                        name="categoria"
                        isMulti
                        isClearable
                        placeholder="Todas"
                        onChange={(values) =>
                            onFilterChange(values, "categoria")
                        }
                    />
                </div>
                <div style={{width: '50px'}} className="filtros-avanzados filtros-avanzados-movil" onClick={displayFilters}>
                    <label>&nbsp;</label>
                    <button className="btn" title="Filtros Avanzados">
                        <i className="zmdi zmdi-filter-list"></i>
                        <span className="d-lg-none">Más filtros</span>
                    </button>
                </div>
                <div id="mas-filtros" className="row col-12 px-0 mx-0 mas-filtros d-none m-b-25 m-t-5">
                    <div className="col-12 col-md-6 col-lg-3">
                        <label className="orden-filter-label">Numeración</label>
                        <Select
                            id="numeracion"
                            options={numeraciones}
                            name="numeracion"
                            isMulti
                            isClearable
                            placeholder="Todas"
                            onChange={(values) =>
                                onFilterChange(values, "numeracion")
                            }
                        />
                    </div>

					<div className="col-12 col-md-6 col-lg-3">
                        <label className="orden-filter-label">Plazo</label>
						<select
							className="form-control"
							name="plazo"
							id="plazo"
							onChange={(e) =>
								onFilterChange(
									e.target.value,
									"plazo"
								)
							}
						>
							<option value="todos">Todos</option>
							{plazos.map((termino) => (
								<option
									value={termino.value}
									key={termino.value}
								>
									{termino.label}
								</option>
							))}
						</select>
                    </div>

					<div className="col-12 col-md-6 col-lg-2 filtros-principales-movil d-none d-lg-block">
						<label className="orden-filter-label">Estado DGII</label>
						<Select
							id="estadoDgii"
							options={[
								{ value: "", label: "Todos" },
								{ value: "REGISTERED", label: "Registrada" },
								{ value: "FINISHED", label: "Finalizada" },
							]}
							name="estadoDgii"
							isClearable
							onChange={(values) => {
								onFilterChange(values.value, "estadoDgii")
							}}
							placeholder="Todos"
						/>
					</div>
                </div>
			</FiltersContainer>


				{isLoading && <Loader />}
				{!isLoading && facturas.data?.length ? (
					<div className="row px-3"
				style={{ overflowX: "auto", paddingBottom: "5rem" }}>
						<FacturasListDesktop
							facturas={facturas}
							openValidarFacturaModal={openValidarFacturaModal}
							openPagarFacturaModal={openPagarFacturaModal}
							openInformacionDgiiModal={openInformacionDgiiModal}
							openPINModal={openPINModal}
							openCancelModal={openCancelModal}
                            actualizarEstado={actualizarEstado}
                            reenviarDGII={reenviarDGII}
							openModalToggleTipoDespacho={openModalToggleTipoDespacho}
						/>

						<FacturasListMobile
							facturas={facturas}
							openValidarFacturaModal={openValidarFacturaModal}
							openPagarFacturaModal={openPagarFacturaModal}
							openPINModal={openPINModal}
							openCancelModal={openCancelModal}
                            actualizarEstado={actualizarEstado}
                            reenviarDGII={reenviarDGII}
							openModalToggleTipoDespacho={openModalToggleTipoDespacho}
						/>

                        {facturas.links && (
                            <Pagination
                                links={facturas.links}
                                meta={facturas.meta}
                                getData={getFacturas}
                            />
                        )}
					</div>
				) : (
					<div>
						{!isLoading && (
							<div className="text-center mt-5 mb-5 w-100">No hay ninguna Factura creada con los filtros aplicados.</div>
						)}
					</div>
				)}

			{/* AGREGAR PAGO MODAL */}
			<Modal
				title="Agregar Pago"
				show={showAgregarPagoModal}
				callback={() => {
					if (!pagoInfo.pago || pagoInfo.pago <= 0 || pagoInfo.pago < facturaSeleccionada.por_cobrar) {
						toast("El monto debe ser mayor o igual al total de la factura.", "error");
						return;
					}
					formRef.current.dispatchEvent(
						new Event("submit", { bubbles: true, cancelable: true })
					);
				}}
				acceptText="Aceptar"
				toggle={() => {
					setPagoInfo({ pago: null, tipo_pago: {} });
					setShowAgregarPagoModal((state) => !state);
				}}
				size="419px"
				footer={
					<PagoModalFooter
						setMostrar={setShowAgregarPagoModal}
						setTipoImpresionAfterPago={setTipoImpresionAfterPago}
						formRef={formRef}
						saving={saving}
					/>
				}
			>
				<form method="post" onSubmit={guardarPago} ref={formRef} className="crear-compra">
                    <PagoForm
                        pagoInfo={pagoInfo}
                        setPagoInfo={setPagoInfo}
                        errores={pagoError}
                        isEdit={false}
                        form={facturaSeleccionada}
                        setPorPagar={() => setPagoInfo((state) => ({
                            ...state,
                            pago: facturaSeleccionada.por_cobrar,
                        }))}
                        sumatoriaFinal={facturaSeleccionada.sumatoria && {
                            descuento: facturaSeleccionada.descuento,
                            // `sumatoria` trae Subtotal/Descuento/Total mezclados con el
                            // desglose por impuesto: pasarlo completo como `impuestos`
                            // hace que el 30% de retención se calcule sobre todo eso
                            // sumado, no sólo sobre los impuestos. "Total ITBIS" ya es el
                            // total de impuestos de la factura calculado en el backend.
                            impuestos: { "Total ITBIS": facturaSeleccionada.sumatoria["Total ITBIS"] },
                            subtotal: facturaSeleccionada.sumatoria.Subtotal,
                            total: facturaSeleccionada.sumatoria.Total,
                            exentoGrabado: facturaSeleccionada.sumatoria
                        }}
                        />
				</form>
			</Modal>

			{/* VALIDAR FACTURA RECURRENTE MODAL */}
			<Modal
				title="Validar Factura Recurrente"
				show={showValidateModal}
				callback={() => validateFactura(facturaSeleccionada.id)}
				acceptText="Validar"
				toggle={() => {
					setShowValidateModal((state) => !state);
				}}
			>
				¿Estás seguro de que deseas validar esta Factura?
			</Modal>

			{/* <---PIN DE SEGURIDAD MODAL--- */}
			<Modal
				title="PIN de seguridad"
				show={showPINModal}
				callback={() => checkPINSeguridad()}
				acceptText="Aceptar"
				toggle={() => setShowPINModal((state) => !state)}
				size="419px"
			>
				<div className="form-group">
					<label>PIN de seguridad</label>
					<input
						type="password"
						className="form-control"
						id="PIN"
						name="pin_seguridad"
						value={pinSeguridad}
						onChange={(e) => setPinSeguridad(e.target.value)}
					/>
					<p
						className="font-weight-bold"
						style={{
							color: "gray",
							fontStyle: "italic",
							lineHeight: "13px",
							fontSize: "0.7rem",
						}}
					>
						Para continuar ingrese PIN de seguridad
					</p>
				</div>
			</Modal>

			{/* Ruta de distribución */}
			<Modal
				title="GENERAR REPORTE RUTA DE DISTRIBUCIÓN"
				show={showModalRutaDistribucion}
				callback={downloadRutaDistribucionReporte}
				acceptText="Generar"
				toggle={() => setShowModalRutaDistribucion((state) => !state)}
				size="650px"
				classes={{
					content: "",
					header: '',
					body: 'overflow-visible',
					footer: ''
				}}
			>
				<div className='row'>
					<div className='col-md-6'>
						<div className='form-group'>
							<label>Desde:</label>
							<input
								type='date'
								name='desde'
								id='desde'
								value={desdeRD}
								onChange={(e) => setDesdeRD(e.target.value)}
								className='form-control'
							/>
						</div>
					</div>
					<div className='col-md-6'>
						<div className='form-group'>
							<label>Hasta:</label>
							<input
								type='date'
								name='hasta'
								value={hastaRD}
								onChange={(e) => setHastaRD(e.target.value)}
								id='hasta'
								className='form-control'
							/>
						</div>
					</div>
				</div>

				<div className='row'>
					<div className='col-md-12'>
						<div className='form-group'>
							<label>Empleado:</label>
							<Select
								id="empleado"
								options={empleados}
								name="empleado"
								onChange={(e) => setEmpleadoRD(e)}
								classNamePrefix="select-menu-z-999"
								styles={{
									control: (styles) => ({
										...styles,
										height: "38px",
									}),
									menu: (styles) => ({
										...styles,
										"z-index": "999",
									}),
								}}
							/>
						</div>
					</div>
				</div>
			</Modal>

			<Modal
				title="Cancelar Factura"
				show={showCancel}
				callback={() => cancelFactura(facturaActual.id)}
				acceptText="Aceptar"
				toggle={closeCancelModal}
			>
				¿Estás seguro de que deseas cancelar esta factura? (Esta acción
				no se puede deshacer).
                <br />
                <br />
                Seleccione el motivo de la cancelación:
                <Select
                    name="razon_estado"
                    menuPosition="fixed"
                    required
                    placeholder="Seleccionar..."
                    onChange={e => {
                        setRazonCancelacion(e.value);
                    }}
                    options={[
                        {label: "01 Deterioro de Factura Pre-Impresa", value: "01 Deterioro de Factura Pre-Impresa"},
                        {label: "02 Errores de Impresión (Factura Pre-Impresa)", value: "02 Errores de Impresión (Factura Pre-Impresa)"},
                        {label: "03 Impresión Defectuosa", value: "03 Impresión Defectuosa"},
                        {label: "04 Duplicidad de Factura", value: "04 Duplicidad de Factura"},
                        {label: "05 Corrección de la Información", value: "05 Corrección de la Información"},
                        {label: "06 Cambio de Productos", value: "06 Cambio de Productos"},
                        {label: "07 Devolución de Productos", value: "07 Devolución de Productos"},
                        {label: "08 Omisión de Productos", value: "08 Omisión de Productos"},
                    ]}
                />
			</Modal>

            <Modal
                show={showInformacionDgiiModal}
                title={`Informacion de la DGII - ${facturaSeleccionada?.numero_comprobante || ''}`}
                size="650px"
                toggle={() => {
					// setInfoMetodoPago({});
					setShowInformacionDgiiModal((state) => !state);
				}}
            >
                <h3>DOCUMENTO</h3>
                {facturaSeleccionada?.documento ? (
                    <table className="table small" style={{lineHeight:1.2}}>
                        <tbody>
                            <tr>
                                <td><strong className="uppercase">Request ID</strong></td>
                                <td>{facturaSeleccionada.documento.alanube_id}</td>
                            </tr>
                            <tr>
                                <td><strong className="uppercase">Estado del Envío</strong></td>
                                <td>
                                    <div>{facturaSeleccionada.documento.status}</div>
                                    <small>
                                        {estado_factura[facturaSeleccionada.documento.status || "FAILED"].descripcion}
                                    </small>
                                </td>
                            </tr>
                            <tr>
                                <td><strong className="uppercase">Estado DGII</strong></td>
                                <td>
                                    <div>{facturaSeleccionada.documento.legalStatus}</div>
                                    <small>
                                        {estado_DGII[facturaSeleccionada.documento.legalStatus || 'FAILED']?.descripcion}
                                    </small>
                                </td>
                            </tr>
                            <tr>
                                <td><strong className="uppercase">Código de Seguridad</strong></td>
                                <td>{facturaSeleccionada.documento.securityCode}</td>
                            </tr>
                            <tr>
                                <td><strong className="uppercase">Archivos</strong></td>
                                <td>
                                    {facturaSeleccionada.documento?.pdf && (
                                        <a href={facturaSeleccionada.documento.pdf} target="_blank">PDF</a>
                                    )}
                                    {facturaSeleccionada.documento.xml ? ' | ' : ''}
                                    {facturaSeleccionada.documento.xml && (
                                        <a href={facturaSeleccionada.documento.xml} target="_blank">XML</a>
                                    )}
                                </td>
                            </tr>
                            {facturaSeleccionada.documento?.governmentResponse?.error && (
                                <tr>
                                    <td><strong className="uppercase">Error</strong></td>
                                    <td>{facturaSeleccionada.documento.governmentResponse?.error}</td>
                                </tr>
                            )}
                            {facturaSeleccionada.documento?.governmentResponse?.value && (
                                <tr>
                                    <td><strong className="uppercase">Mensaje</strong></td>
                                    <td>{facturaSeleccionada.documento.governmentResponse?.value?.map(v => <div key={v}>Codigo {v.codigo} - {v.valor}</div>)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                ) : (
                    <div>No hay ningun documento registrado.</div>
                )}
                {facturaSeleccionada?.cancelacion && (
                    <>
                        <hr />
                        <h3>CANCELACIÓN</h3>
                        <table className="table small" style={{lineHeight:1.2}}>
                            <tbody>
                                <tr>
                                    <td><strong className="uppercase">Fecha</strong></td>
                                    <td>{facturaSeleccionada.cancelacion.stampDate}</td>
                                </tr>
                                <tr>
                                    <td><strong className="uppercase">Request ID</strong></td>
                                    <td>{facturaSeleccionada.cancelacion?.alanube_id}</td>
                                </tr>
                                <tr>
                                    <td><strong className="uppercase">Estado del Envío</strong></td>
                                    <td>
                                        <div>{facturaSeleccionada.cancelacion?.status}</div>
                                        <small>
                                            {estado_factura[facturaSeleccionada.cancelacion?.status]?.descripcion}
                                        </small>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong className="uppercase">Mensaje</strong></td>
                                    <td>
                                        <div>Código: {facturaSeleccionada.cancelacion.governmentResponse?.codigo}</div>
                                        <div>Nombre: {facturaSeleccionada.cancelacion.governmentResponse?.nombre}</div>
                                        <ul>
                                            {facturaSeleccionada.cancelacion.governmentResponse?.mensajes?.map(mensaje => (
                                                <li>{mensaje}</li>
                                            ))}
                                        </ul>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong className="uppercase">Archivos</strong></td>
                                    <td>
                                        {facturaSeleccionada.cancelacion.xml && (
                                            <a href={facturaSeleccionada.cancelacion.xml} target="_blank">XML</a>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </Modal>

			{/* Toggle Tipo despacho MODAL */}
			<Modal
				title="Cambiar tipo de entrega"
				show={showModalTipoDespacho}
				callback={toggleTipoDespacho}
				acceptText="Validar"
				toggle={closeModalToggleTipoDespacho}
			>
				¿Estás seguro de que deseas cambiar el tipo de entrega de
				<strong className="ml-1">{facturaTipoDespacho?.tipo_despacho === "Carga" ? "Domicilio" : "Local"}</strong> a
				<strong className="ml-1">{facturaTipoDespacho?.tipo_despacho === "Carga" ? "Local" : "Domicilio"}</strong> para la factura <strong>{facturaTipoDespacho?.numero_comprobante}</strong> de <strong>{facturaTipoDespacho?.cliente?.nombre}</strong>?
			</Modal>

		</>
	);
};

const FacturasListDesktop = ({
	facturas,
	openValidarFacturaModal,
	openPagarFacturaModal,
	openInformacionDgiiModal,
	openPINModal,
    actualizarEstado,
    reenviarDGII,
	openModalToggleTipoDespacho,
}) => {
	return (
		<div
			className="table-data table-responsive table-row__desktop"
			style={{ overflowX: "visible" }}
		>
			<table className="table table-data2">
				<thead>
					<tr>
						<th>NCF/Numero</th>
						<th>Cliente</th>
						<th>Fecha</th>
						<th>Vencimiento</th>
						<th>Total</th>
						{/* <th>Balance</th> */}
						{!isVendedor() && <th>Vendedor</th>}
						<th>Zona</th>
						<th className="text-center">Estado</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					{facturas.data.map((factura) => <FacturaListItem
                        factura={factura}
                        openValidarFacturaModal={openValidarFacturaModal}
                        openPagarFacturaModal={openPagarFacturaModal}
                        openInformacionDgiiModal={openInformacionDgiiModal}
                        openPINModal={openPINModal}
                        reenviarDGII={reenviarDGII}
                        actualizarEstado={actualizarEstado}
						openModalToggleTipoDespacho={openModalToggleTipoDespacho}
                    />)}
				</tbody>
			</table>
		</div>
	);
};

const FacturaListItem = ({
    factura,
    openValidarFacturaModal,
    openPagarFacturaModal,
    openInformacionDgiiModal,
    openCancelModal,
	openPINModal,
    reenviarDGII,
    actualizarEstado,
	openModalToggleTipoDespacho
}) => {
    const [procesando, setProcesando] = useState(false);

    const estadoDGII = estado_DGII[factura.documento?.legalStatus || 'NOT_SENT'];
    return (
        <tr className="tr-shadow" key={factura.id}>
            {factura.recurrencia_id &&
            factura.validada === "no" ? (
                <td></td>
            ) : (
                <td>
                    <div>{factura.numero_comprobante}</div>
                    {factura.electronica ? (
                        <>
                            <div className={`dropdown`}>
                                <button className={`badge uppercase badge-${estadoDGII.class}`}>
                                    {factura.documento ? estadoDGII.label : 'No enviada'} <i className="zmdi zmdi-chevron-down"></i>
                                </button>
                                <div
                                    className={`dropdown-menu`}
                                >
                                    {factura?.documento?.alanube_id && (
                                        <button
                                            className="dropdown-item"
                                            onClick={() => openInformacionDgiiModal(factura)}
                                        >
                                            Informacion DGII
                                        </button>
                                    )}
                                    {factura?.documento?.alanube_id && (
                                        <button
                                            className="dropdown-item"
                                            onClick={async () => {
                                                setProcesando(true);
                                                actualizarEstado(factura.documento.alanube_id);
                                                setProcesando(false);
                                            }}
                                        >
                                            Actualizar Estado
                                        </button>
                                    )}
                                    {(factura.electronica && !factura?.documento) || (factura.electronica && factura.documento.status === 'FAILED') ? (
                                        <button
                                            className="dropdown-item"
                                        onClick={() => {
                                            setProcesando(true);
                                            reenviarDGII(factura.id).then(() => {
                                                setProcesando(false);
                                            })
                                        }}
                                        >
                                            Enviar a la DGII
                                        </button>
                                    ) : ""}
                                    {factura?.documento?.documentStampUrl && (
                                        <a href={factura.documento.documentStampUrl} target="_blank" className="dropdown-item">
                                            Consultar Status DGII
                                        </a>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : ''}
                </td>
            )}

            <td>
                <Link
                    to={getLink(`/contactos/${factura.cliente?.id}/transacciones`)}
                    className="ml-2 contacto"
                    onClick={() => {localStorage.setItem("contacto", JSON.stringify(factura.cliente));}}
                >
                    {factura.cliente?.codigo} - {factura.cliente?.nombre}
                </Link>
				<div className="ml-2 h-100 w-100 d-flex items-center" >
					<span><strong>Seguimiento:</strong> {factura.seguimiento}</span>
				</div>
				{factura.planificador_tarea_id && <PlanificadorTareaEstatus id={factura.planificador_tarea_id}></PlanificadorTareaEstatus>}
            </td>
            <td>
                <div>
                    {dayjs(factura.fecha).format(
                        "DD/MM/YYYY"
                    )}
                </div>
                <div
                    className={`${
                        !!factura.recurrencia_id &&
                        factura.validada === "si"
                            ? "d-block"
                            : "d-none"
                    }`}
                >
                    <span className="fw-600">
                        Emisión:{" "}
                    </span>
                    {dayjs(factura.fecha_emision).format(
                        "DD/MM/YYYY"
                    )}
                </div>
            </td>
            <td>
                {dayjs(factura.fecha_vencimiento).format(
                    "DD/MM/YYYY"
                )}
            </td>
            <td>{formatCurrency(factura.total, "$")}</td>
            {/* <td>
                {formatCurrency(factura.por_cobrar, "$")}
            </td> */}
            {!isVendedor() && (
                <>
                    {factura.vendedor ? (
                        <td style={{lineHeight: "1.1"}}>
                            {`${factura.vendedor?.nombre} ${factura.vendedor?.apellido}`} <br/>{factura.usuario && (<small>{`Creada por: ${factura.usuario?.username}`}</small>)}
                        </td>
                    ) : (
                        <td></td>
                    )}
                </>
            )}

            <td>{factura.ruta?.nombre}</td>
            <td className="text-center">
                {renderEstadoBadge(factura)} <br/>

				<span className={`badge p-2 mt-1 ${factura.cuadre_caja_id ? "badge-success" : "badge-secondary"}`} style={{width: "80px"}}>
					{factura.cuadre_caja_id ? "Cuadrada" : "Sin Cuadrar"}
				</span>

                <small
                    className={`text-success uppercase ${
                        factura.estado === "pendiente" &&
                        factura.recurrencia_id &&
                        factura.validada === "si"
                            ? "d-block"
                            : "d-none"
                    }`}
                >
                    Validada
                </small>
            </td>
            <td className="text-right">
                {procesando ? (
                    <Loader type="small" />
                ) : (
                    <Dropdown fixed>
                        {canValidate(factura) ? (
                            <button
                                className="dropdown-item"
                                onClick={() =>
                                    openValidarFacturaModal(
                                        factura
                                    )
                                }
                            >
                                Validar
                            </button>
                        ) : (
                            <>
                                {canPay(factura) && (
                                    <button
                                        className="dropdown-item"
                                        onClick={() =>
                                            openPagarFacturaModal(
                                                factura
                                            )
                                        }
                                    >
                                        Pagar
                                    </button>
                                )}
                                <Link
                                    to={getLink(
                                        `/imprimir/facturas/${factura.id}?original=1`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                    title="Impresión normal"
                                >
                                    Original
                                </Link>
                                <Link
                                    to={getLink(
                                        `/imprimir/facturas/${factura.id}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                    title="Impresión normal"
                                >
                                    Impresión
                                </Link>
                                {/* <Link
                                    to={getLink(
                                        `/imprimir/facturas/${factura.id}/doble`
                                    )}
                                    target="_blank"
                                    className="dropdown-item d-none d-sm-inline"
                                    title="Impresión Doble"
                                >
                                    Impresión doble
                                </Link> */}

                                {factura.pagos.length > 0 && <Link
                                        to={getLink(
                                            `/consultas/historial-pagos-facturas/${factura.id}`
                                        )}
                                        target="_blank"
                                        className="dropdown-item"
                                    >
                                        Historial de Pagos
                                    </Link>
                                }
                                {factura.facturaFirmada?.length > 0 && <a
									href={factura.facturaFirmada[0].url}
									className="dropdown-item"
									target="_blank" rel="noreferrer"
								>
									Factura Firmada
								</a>}

                                {(isVendedor() && isDayDate(factura.fecha) && factura.estado === "pendiente" && !factura.electronica)
								 || (!isVendedor() && !factura.electronica && factura.estado === "pendiente")
								 || (!isVendedor() && factura.electronica && !factura.documento?.id && factura.estado === "pendiente")
									? (
										<Link
											to={getLink(
												`/ingresos/facturas/${factura.id}/edit`
											)}
											className="dropdown-item"
											title="Editar"
										>
											Editar
										</Link>
									) : (!factura.electronica && factura.estado !== "cancelada" && factura.estado !== "incobrable" &&
										<button
											type="button"
											className="dropdown-item"
											title="Cancelar"
											onClick={() => openPINModal(factura)}
										>
											Cancelar
										</button>
									)
								}

                                {
									!factura.despacho_id && <button
                                        className="dropdown-item"
                                        onClick={() =>
                                            openModalToggleTipoDespacho(
                                                factura
                                            )
                                        }
                                    >
                                        Local/Domicilio
                                    </button>
								}
                            </>
                        )}
                    </Dropdown>
                )}
            </td>
        </tr>
    );
}

const FacturasListMobile = ({
	facturas,
    openInformacionDgiiModal,
	openValidarFacturaModal,
	openPagarFacturaModal,
	openPINModal,
	openCancelModal,
	reenviarDGII,
    actualizarEstado,
	openModalToggleTipoDespacho,
}) => {
    const [procesando, setProcesando] = useState(false);
	return (
		<div
			className="table-row__mobile w-100"
			style={{ overflowX: "visible" }}
		>
        {facturas.data.map((factura) => {
            const estadoDGII = estado_DGII[factura.documento?.legalStatus || 'NOT_SENT'];
            return (
                <div className="col-12" key={factura.id}>
                    <fieldset
                        className="fieldset px-3"
                        style={{ border: "1px solid rgba(0,138,171,0.8)", margin: '0 0 20px' }}
                    >
                        <legend>{factura.numero_comprobante}</legend>
                        {factura.electronica ? (
                            <div className="dropdown mb-2">
                                <button className={`badge uppercase badge-${estadoDGII.class}`}>
                                    {factura.documento ? estadoDGII.label : 'No enviada'} <i className="zmdi zmdi-chevron-down"></i>
                                </button>
                                <div
                                    className={`dropdown-menu`}
                                >
                                    {factura?.documento?.alanube_id && (
                                        <button
                                            className="dropdown-item"
                                            onClick={() => openInformacionDgiiModal(factura)}
                                        >
                                            Informacion DGII
                                        </button>
                                    )}
                                    {factura?.documento?.alanube_id && (
                                        <button
                                            className="dropdown-item"
                                            onClick={async () => {
                                                setProcesando(true);
                                                actualizarEstado(factura.documento.alanube_id);
                                                setProcesando(false);
                                            }}
                                        >
                                            Actualizar Estado
                                        </button>
                                    )}
                                    {(factura.electronica && !factura?.documento) || (factura.electronica && factura.documento.status === 'FAILED') ? (
                                        <button
                                            className="dropdown-item"
                                        onClick={() => {
                                            setProcesando(true);
                                            reenviarDGII(factura.id).then(() => {
                                                setProcesando(false);
                                            })
                                        }}
                                        >
                                            Enviar a la DGII
                                        </button>
                                    ) : ""}
                                    {factura?.documento?.documentStampUrl && (
                                        <a href={factura.documento.documentStampUrl} target="_blank" className="dropdown-item">
                                            Consultar Status DGII
                                        </a>
                                    )}
                                </div>
                            </div>
                        ) : ''}
                        <div className="row">
                            <div className="form-group col-12 mb-1">
                                <label className="orden-filter-label d-flex justify-content-between">
                                    <span
                                        className={`text-success pr-2 font-weight-normal ${
                                            factura.estado === "pendiente" &&
                                            factura.recurrencia_id &&
                                            factura.validada === "si"
                                                ? "d-block"
                                                : "d-none"
                                        }`}
                                        style={{ fontSize: "0.9rem" }}
                                    >
                                        Validada
                                    </span>
                                </label>
                                <div>
                                    <strong>{factura.cliente?.nombre}</strong>
                                </div>
                            </div>
                        </div>
                        <table style={{ width: '100%', tableLayout: 'fixed'}}>
                            <tbody>
                                <tr>
                                    <td className="pb-1"><small className="orden-filter-label uppercase">Fecha emisión</small></td>
                                    <td className="pb-1 text-right">{dayjs(factura.fecha).format("DD/MM/YYYY")}</td>
                                </tr>
                                <tr>
                                    <td className="pb-1"><small className="orden-filter-label uppercase">Estado</small></td>
                                    <td className="pb-1 text-right text-capitalize">{obtenerTextoEstado(factura)}</td>
                                </tr>
                                <tr>
                                    <td className="pb-1"><small className="orden-filter-label uppercase">Vencimiento</small></td>
                                    <td className="pb-1 text-right">{dayjs(factura.fecha_vencimiento).format("DD/MM/YYYY")}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="row">
                            <div className="form-group col-7 pr-1 mb-1">
                                <label className="orden-filter-label uppercase">
                                    Total
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cliente"
                                    value={formatCurrency(factura.total, "$")}
                                    style={{ backgroundColor: "#fff" }}
                                    className="form-control"
                                    readOnly
                                />
                            </div>
                            <div className="form-group col-5 pl-1 mb-1">
                                <label>
                                    <br />
                                </label>
                                <Dropdown
                                    classes={{ button: "w-100" }}
                                    styles={{
                                        button: {
                                            height: "38px",
                                            lineHeight: "0px",
                                            border: "1px solid #FE9D1F",
                                        },
                                        dropdownMenu: {
                                            zIndex: "99",
                                        },
                                    }}
                                >
                                    {canValidate(factura) ? (
                                        <button
                                            className="dropdown-item"
                                            onClick={() =>
                                                openValidarFacturaModal(factura)
                                            }
                                        >
                                            Validar
                                        </button>
                                    ) : (
                                        <>
                                            {canPay(factura) && (
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() =>
                                                        openPagarFacturaModal(
                                                            factura
                                                        )
                                                    }
                                                >
                                                    Pagar
                                                </button>
                                            )}
                                            {canPrint(factura) ? (
                                                <Link
                                                    to={getLink(
                                                        `/imprimir/facturas/${factura.id}`
                                                    )}
                                                    target="_blank"
                                                    className="dropdown-item"
                                                    title="Impresión normal"
                                                >
                                                    Impresión
                                                </Link>
                                            ) : (
                                                <Link
                                                    to={getLink(
                                                        `/imprimir/facturas/${factura.id}`
                                                    )}
                                                    target="_blank"
                                                    className="dropdown-item"
                                                    title="Impresión normal"
                                                >
                                                    Impresión
                                                </Link>
                                            )}
                                            {canPrint(factura) ? (
                                                <Link
                                                    to={getLink(
                                                        `/imprimir/facturas/${factura.id}/doble`
                                                    )}
                                                    target="_blank"
                                                    className="dropdown-item d-none d-sm-inline"
                                                    title="Impresión Doble"
                                                >
                                                    Impresión doble
                                                </Link>
                                            ) : (
                                                <Link
                                                    to={getLink(
                                                        `/imprimir/facturas/${factura.id}/doble`
                                                    )}
                                                    target="_blank"
                                                    className="dropdown-item d-none d-sm-inline"
                                                    title="Impresión Doble"
                                                >
                                                    Impresión doble
                                                </Link>
                                            )}
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                title="Cancelar"
                                                onClick={() => openPINModal(factura)}
                                            >
                                                Cancelar
                                            </button>
                                        </>
                                    )}

                                    {
                                        !factura.despacho_id && <button
                                            className="dropdown-item"
                                            onClick={() =>
                                                openModalToggleTipoDespacho(
                                                    factura
                                                )
                                            }
                                        >
                                            Local/Domicilio
                                        </button>
                                    }
                                </Dropdown>
                            </div>
                        </div>
                    </fieldset>
                </div>
            )
        })}
		</div>
	);
};

export const PagoModalFooter = React.memo(
	({
		setMostrar,
		setTipoImpresionAfterPago,
		formRef = null,
		guardarYSalir,
		saving,
	}) => {
		const submitForm = async (type) => {
			await setTipoImpresionAfterPago(type);
			if (formRef) {
				formRef.current.dispatchEvent(
					new Event("submit", { bubbles: true, cancelable: true })
				);
				return;
			}
			guardarYSalir("pagar");
		};
		return (
			<>
				<button
					type="button"
					onClick={() => {
						setMostrar(false);
					}}
					className="btn btn-plain"
				>
					Cancelar
				</button>
				{saving ? (
					<Loader type="small" />
				) : (
					<>
						<button
							onClick={() => submitForm("normal")}
							type="button"
							className="btn btn-primary"
                            style={{gridColumn: 2}}
						>
							Imprimir
						</button>
						<button
							onClick={() => submitForm("doble")}
							type="button"
							className="btn btn-primary d-md-none"
                            style={{gridColumn: 2, gridRow: 1}}
						>
							Imprimir doble
						</button>
					</>
				)}
			</>
		);
	}
);

export default Facturas;
